import { AxiosRequestConfig } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import {
  AllProtectedDomainsResponse,
  DomainTypes,
  FetchProtectedDomainPayload,
  GetProtectedDomainsPayload,
  ProtectedDomainsListState,
  ProtectedDomainStatusItem,
  ProtectedDomainTypeItem,
  UpdateProtectedDomainPayload,
  UploadCSVFilePayload,
} from '../types/ProtectedDomainsTypes';

export const getProtectedDomainsStatus = async () => {
  const config = {
    method: 'GET',
    url: 'v2/companies/protections/status',
  };
  return apiClient(config);
};

export const getProtectedDomainsTypes = async () => {
  const config = {
    method: 'GET',
    url: 'v2/companies/protections/types',
  };
  return apiClient(config);
};

export const getCompanyProtectedDomains = async (
  payload: GetProtectedDomainsPayload,
) => {
  const { companyId, domainName, hideDeactivated, statusId, page, size } =
    payload;

  const params = new URLSearchParams();

  if (domainName) params.append('domain_name', domainName);
  params.append('hide_deactivated', String(hideDeactivated ?? false));
  if (statusId !== undefined) params.append('status_id', statusId.toString());
  if (page !== undefined) params.append('page', page.toString());
  if (size !== undefined) params.append('size', size.toString());

  const config = {
    method: 'GET',
    url: `v2/companies/${companyId}/protections?${params.toString()}`,
  };
  return apiClient(config);
};

export const getProtectedDomainsCSVTemplate = async () => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url: 'v2/companies/protections/upload/template',
    responseType: 'blob',
  };
  return apiClient(config);
};

export const uploadProtectedDomainsCSV = async (
  payload: UploadCSVFilePayload,
) => {
  const { companyId, data } = payload;
  const config = {
    method: 'POST',
    url: `v2/companies/${companyId}/protections/upload`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
  };
  return apiClient(config);
};

export const addProtectedDomain = async (payload: DomainTypes) => {
  const { companyId, data } = payload;
  const config = {
    method: 'POST',
    url: `v2/companies/${companyId}/protections`,
    data,
  };
  return apiClient(config);
};

export const updateProtectedDomain = async (
  payload: UpdateProtectedDomainPayload,
) => {
  const { companyId, protection_id, request } = payload;
  const config = {
    method: 'PUT',
    url: `v2/companies/${companyId}/protections/${protection_id}`,
    data: request,
  };
  return apiClient(config);
};

export const fetchProtectedDomainsApi = async (
  payload: FetchProtectedDomainPayload,
) => {
  const { companyId, hideDeactivated, domainName } = payload;

  const params = new URLSearchParams();
  params.append('hide_deactivated', String(hideDeactivated ?? false));
  if (domainName) params.append('domain_name', domainName);

  const config = {
    method: 'GET',
    url: `v2/companies/${companyId}/protections/all?${params.toString()}`,
    headers: {
      accept: 'application/json',
    },
  };

  return apiClient(config);
};

export const protectedDomainsStatusQueryKey = ['protectedDomains', 'status'];
export const protectedDomainsTypesQueryKey = ['protectedDomains', 'types'];

/** Deactivated is excluded from the onboarding-pending filter, matching the old saga. */
export const useProtectedDomainsStatusQuery = () =>
  useQuery<ProtectedDomainStatusItem[]>({
    queryKey: protectedDomainsStatusQueryKey,
    queryFn: async () => {
      const response = await getProtectedDomainsStatus();
      return (response?.data?.items ?? []).filter(
        (status: ProtectedDomainStatusItem) =>
          status.name !== 'onboardingPending',
      );
    },
  });

export const useProtectedDomainsTypesQuery = () =>
  useQuery<{ items: ProtectedDomainTypeItem[] }>({
    queryKey: protectedDomainsTypesQueryKey,
    queryFn: async () => {
      const response = await getProtectedDomainsTypes();
      return response?.data;
    },
  });

export const protectedDomainsListQueryKey = (
  payload: Partial<GetProtectedDomainsPayload> & { companyId?: string },
) => [
  'protectedDomains',
  'list',
  payload.companyId,
  payload.domainName ?? '',
  payload.hideDeactivated ?? false,
  payload.statusId ?? null,
  payload.page,
  payload.size,
];

/**
 * Paginated, filterable protected domains list backing the Protected Domains
 * tab table. `staleTime: 0` so revisiting a previous filter/toggle combination
 * refetches (matching the old always-fetch Redux behaviour) instead of showing
 * a possibly stale cached page.
 */
export const useProtectedDomainsListQuery = (
  payload: GetProtectedDomainsPayload,
  enabled = true,
) =>
  useQuery<ProtectedDomainsListState['data']>({
    queryKey: protectedDomainsListQueryKey(payload),
    queryFn: async () => {
      const response = await getCompanyProtectedDomains(payload);
      return response?.data;
    },
    enabled: enabled && !!payload.companyId,
    staleTime: 0,
  });

export const allProtectedDomainsQueryKey = (
  payload: FetchProtectedDomainPayload,
) => [
  'protectedDomains',
  'all',
  payload.companyId,
  payload.hideDeactivated ?? false,
  payload.domainName ?? '',
];

/** Unpaginated "all protections" list used for dropdown options and tab counts. */
export const useAllProtectedDomainsQuery = (
  payload: FetchProtectedDomainPayload,
  enabled = true,
) =>
  useQuery<AllProtectedDomainsResponse>({
    queryKey: allProtectedDomainsQueryKey(payload),
    queryFn: async () => {
      const response = await fetchProtectedDomainsApi(payload);
      return response?.data;
    },
    enabled: enabled && !!payload.companyId,
  });

const invalidateDomainLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  companyId?: string,
) => {
  queryClient.invalidateQueries({
    queryKey: ['protectedDomains', 'list', companyId],
  });
  queryClient.invalidateQueries({
    queryKey: ['protectedDomains', 'all', companyId],
  });
};

/** Adds one or more protected domains manually; refreshes list + all-domains caches. */
export const useAddProtectedDomainMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DomainTypes) => addProtectedDomain(payload),
    onSuccess: (_data, variables) =>
      invalidateDomainLists(queryClient, variables.companyId),
  });
};

/** Updates (or soft-deletes via status change) a single protected domain. */
export const useUpdateProtectedDomainMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateProtectedDomainPayload) =>
      updateProtectedDomain(payload),
    onSuccess: (_data, variables) =>
      invalidateDomainLists(queryClient, variables.companyId),
  });
};

/** Bulk-adds protected domains via CSV upload. */
export const useUploadProtectedDomainsCSVMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UploadCSVFilePayload) =>
      uploadProtectedDomainsCSV(payload),
    onSuccess: (_data, variables) =>
      invalidateDomainLists(queryClient, variables.companyId),
  });
};
